<template>
	<div class="h-full flex flex-col">
		<!-- Loading state -->
		<div v-if="loading" class="flex items-center justify-center py-12">
			<span class="loading loading-spinner loading-md"></span>
		</div>

		<!-- Empty state -->
		<div v-else-if="lessonPlans.length === 0" class="flex items-center justify-center py-12">
			<p class="text-sm text-base-content/60">{{ $t('This collection is empty') }}</p>
		</div>

		<LessonPlanList
			v-else
			:lessonPlans="lessonPlans"
			:withHeader="false"
			:withCollectionsSelector="!isAgendaContext() && usersStore.me?.id !== authorId"
			:showCreatorName="false"
			:page="lessonPlanPage || undefined"
			:loading="loading"
			@lessonPlanClicked="openLessonPlan($event)"
			@requestPage="goToPage"
			@loadMore="loadMorePlans">
		</LessonPlanList>
	</div>

	<LessonPlanViewer
		v-if="showLessonPlanViewer"
		:lessonPlan="selectedLessonPlan"
		:withAddToLibraryButton="true"
		:withSelectButtons="isAgendaContext()"
		:initialWidth="isAgendaContext() ? '2xl' : '3xl'"
		:withCollectionSelector="!isAgendaContext() && selectedLessonPlan?.isSavedInLib"
		:collectionTooltipMessage="collectionTooltipMessage"
		@requestUnmount="closeLessonPlanViewer"
		@addToLibrary="addLessonPlanToLibrary"
		@removedFromLibrary="onRemovedFromLibrary"
		@selectLessonPlan="$emit('selectLessonPlan', $event)"
		@createOwnVersion="$emit('createOwnVersion', $event)"
	/>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { useLessonPlanCollectionsStore } from '@/stores/LessonPlanCollections.store';
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import LessonPlanViewer from '@/components/LessonPlanViewer.vue';
import { useLessonPlansStore } from '@/stores/LessonPlans.store';
import { useUsersStore } from '@/stores/Users.store';
import { LessonPlan } from '@/models/LessonPlan.model';
import { PaginationState } from '@/models/PaginationState.model';
import { ApiErrorMessageBag } from '@/stores/errors/ApiErrorMessageBag';
import { notify } from 'notiwind';
import LessonPlanList from './LessonPlanList.vue';
import { OrderDirection, OrderParameter } from '@/models/OrderParameter.model';

export default {

	name: 'LessonPlanDiscoverAuthorCollection',
	emits: ['selectLessonPlan', 'createOwnVersion'],

	components: {
		LessonPlanViewer,
		LessonPlanList,
	},

	props: {
		collectionId: {
			type: String,
			required: true,
		},
		authorId: {
			type: String,
			required: true,
		},
	},

	data() {
		return {
			loading: false,
			lessonPlans: [] as LessonPlan[],
			lessonPlanPage: null as PaginationState | null,
			showLessonPlanViewer: false,
			selectedLessonPlan: undefined as LessonPlan | undefined,
			justAddedToLibrary: false,
		};
	},

	async created() {
		await this.loadPlans();
	},

	computed: {
		...mapStores(useLessonPlansStore, useDiscoverStateStore, useLessonPlanCollectionsStore, useUsersStore),

		// Show collection message if user just added a lesson plan to the library
		collectionTooltipMessage() {
			if (!this.justAddedToLibrary) return '';
			return this.$t('Add this lesson plan to some library collections?');
		}
	},

	methods: {
		isAgendaContext(): boolean {
			return this.$router.getRootComponentName() === 'Agenda';
		},

		async loadPlans() {
			if (!this.collectionId) return;
			this.loading = true;
			try {
				// Fixed alphabetical ordering in this specific case
				const order = new OrderParameter('title', OrderDirection.ASC);
				const result = await this.lessonPlansStore.loadByCollection(this.collectionId, this.lessonPlanPage, order);
				this.lessonPlans = result.items;
				this.lessonPlanPage = result.page;
			} catch (e: any) {
				if (e instanceof ApiErrorMessageBag) {
					notify({
						text: e.message || this.$t('An error occurred'),
						type: 'error',
					});
				}
			} finally {
				this.loading = false;
			}
		},

		async goToPage(page: PaginationState) {
			this.lessonPlanPage = page;
			await this.loadPlans();
		},

		async loadMorePlans() {
			if (this.loading || !this.lessonPlanPage?.hasNext()) return;
			this.loading = true;
			try {
				const nextPage = this.lessonPlanPage.getNext();
				const result = await this.lessonPlansStore.loadByCollection(this.collectionId, nextPage);
				this.lessonPlans.push(...result.items);
				this.lessonPlanPage = result.page;
				// Adjust 'from' to reflect all loaded items
				if (this.lessonPlanPage) {
					this.lessonPlanPage.from = 1;
				}
			} catch (e: any) {
				if (e instanceof ApiErrorMessageBag) {
					notify({
						text: e.message || this.$t('An error occurred'),
						type: 'error',
					});
				}
			} finally {
				this.loading = false;
			}
		},

		openLessonPlan(lessonPlan: LessonPlan) {
			this.selectedLessonPlan = lessonPlan;
			this.showLessonPlanViewer = true;
		},

		closeLessonPlanViewer() {
			this.showLessonPlanViewer = false;
			this.selectedLessonPlan = undefined;
			this.justAddedToLibrary = false;
		},

		onRemovedFromLibrary(lessonPlan: LessonPlan) {
			lessonPlan.isSavedInLib = false;
		},

		async addLessonPlanToLibrary(lessonPlan: LessonPlan) {
			try{
				if (!lessonPlan) return;
				// await this.lessonPlansStore.save(lessonPlan);
				if (lessonPlan.id) {
					await this.lessonPlansStore.addToLibrary(lessonPlan);
				} else {
					await this.lessonPlansStore.save(lessonPlan);
				}
				lessonPlan.isSavedInLib = true;
				this.justAddedToLibrary = true;

				this.$notify({
					text: this.$t("Lesson plan saved to library"),
					type: "success",
				});
			}
			catch(e:any){
				if (e instanceof ApiErrorMessageBag) {
					notify({
						text: e.toString() || this.$t('Failed to save to library'),
						type: 'error',
					});
				} else {
					console.error(e);
					notify({
						text: this.$t('Failed to save to library'),
						type: 'error',
					});
				}
			}
		},
	},
};
</script>
